import { brandFeatures, withOverrides, type AccessMode, type BrandId, type ReaderFeatures } from "@msb/reader-bridge";

/**
 * Paramètres d'ouverture, passés dans l'URL par le site (fiche produit, « Ma bibliothèque »).
 *
 *   /?manifest=<url du manifest>&mode=excerpt|full&brand=mysmartbook|chatnovel
 *     &smartbook=1&product=<id produit Shopware>&buy=<url fiche produit>&back=<url de retour>&embed=1
 *     #chat_token=<jeton chatbot>
 *
 * Le manifest est une URL du serveur de publications (`server/`) contenant un jeton signé
 * par le site : le reader ne décide pas des droits, il les reçoit.
 *
 * Le jeton du chatbot passe dans le fragment (`#…`) : il n'est jamais envoyé aux serveurs
 * ni écrit dans leurs journaux. Le reader le retire aussitôt de la barre d'adresse.
 */
export interface OpenParams {
  manifestUrl: string;
  accessMode: AccessMode;
  brand: BrandId;
  isSmartbook: boolean;
  features: ReaderFeatures;
  buyUrl?: string;
  backUrl?: string;
  /** Identifiant produit Shopware, utilisé par le chatbot pour retrouver la conversation. */
  productId?: string;
  /** Jeton « reader-application » pour l'API chatbot (mode complet uniquement). */
  chatToken?: string;
  /** Livre ouvert depuis la bibliothèque : sa progression y est affichée. */
  libraryBookId?: string;
  /** Ouvert depuis une discussion de la bibliothèque : le chat est affiché d'emblée. */
  openChat?: boolean;
  /**
   * Extrait affiché dans la fiche produit du site (iframe) : le site porte déjà le titre et l'achat,
   * le reader ne garde que le Sommaire et les Réglages.
   */
  embedded?: boolean;
}

/** Lien refusé : livre absent, lien invalide ou source non autorisée (texte dans OpenError.tsx). */
export type ParamsError = "missing" | "invalid" | "untrusted";

export type ParamsResult = { ok: true; params: OpenParams } | { ok: false; error: ParamsError };

const brands = Object.keys(brandFeatures) as BrandId[];

export function parseOpenParams(search: string, allowedManifestOrigins: string[], hash = ""): ParamsResult {
  const query = new URLSearchParams(search);
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  const manifest = query.get("manifest");
  if (!manifest) return { ok: false, error: "missing" };

  let manifestUrl: URL;
  try {
    manifestUrl = new URL(manifest);
  } catch {
    return { ok: false, error: "invalid" };
  }
  if (allowedManifestOrigins.length > 0 && !allowedManifestOrigins.includes(manifestUrl.origin)) {
    return { ok: false, error: "untrusted" };
  }

  const brandParam = query.get("brand") ?? "mysmartbook";
  const brand = (brands as string[]).includes(brandParam) ? (brandParam as BrandId) : "mysmartbook";
  const accessMode: AccessMode = query.get("mode") === "full" ? "full" : "excerpt";

  return {
    ok: true,
    params: {
      manifestUrl: manifestUrl.toString(),
      accessMode,
      brand,
      isSmartbook: query.get("smartbook") === "1",
      features: withOverrides(brandFeatures[brand], undefined),
      buyUrl: safeHttpUrl(query.get("buy")),
      backUrl: safeHttpUrl(query.get("back")),
      embedded: accessMode === "excerpt" && query.get("embed") === "1",
      productId: safeId(query.get("product")),
      // Jamais de jeton chatbot pour un extrait.
      chatToken: accessMode === "full" ? fragment.get("chat_token") || undefined : undefined,
    },
  };
}

function safeId(value: string | null): string | undefined {
  return value && /^[A-Za-z0-9_-]{1,64}$/.test(value) ? value : undefined;
}

function safeHttpUrl(value: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}
